import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ColorSet, Scheme, colorsApp, setCurrentColors } from 'src/theme/colors';

interface ThemeSchemeContextInterface {
  scheme: Scheme;
  colors: ColorSet;
  setScheme: (scheme: Scheme) => void;
}

const ThemeSchemeContext = React.createContext<ThemeSchemeContextInterface | undefined>(undefined);

/** DOM side effects previously performed by the Redux theme reducer — verbatim. */
const applySchemeToDom = (scheme: Scheme) => {
  document.body.style.backgroundColor = colorsApp[scheme].white;

  const scrollbarStyle = document.createElement('style');
  scrollbarStyle.id = 'scrollbar-style';
  scrollbarStyle.innerHTML = `
        *:not(#main):not(.unstyled-scroll)::-webkit-scrollbar {
          width: 4px !important;
          height: 4px !important;
        }
        *:not(#main):not(.unstyled-scroll)::-webkit-scrollbar-track {
          background: transparent;
        }
        *:not(#main):not(.unstyled-scroll)::-webkit-scrollbar-thumb {
          background: ${colorsApp[scheme].stroke};
          border-radius: 6px;
        }
        *:not(#main):not(.unstyled-scroll)::-webkit-scrollbar-thumb:hover {
          background: ${colorsApp[scheme].davysGrey};
        }
      `;

  const existingStyle = document.getElementById('scrollbar-style');
  if (existingStyle) {
    existingStyle.remove();
  }

  document.head.appendChild(scrollbarStyle);
};

export const ThemeSchemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [scheme, setSchemeState] = useState<Scheme>('light');

  const setScheme = useCallback((next: Scheme) => {
    setSchemeState(next);
    setCurrentColors(colorsApp[next]);
    applySchemeToDom(next);
  }, []);

  const value = useMemo(() => ({ scheme, colors: colorsApp[scheme], setScheme }), [scheme, setScheme]);

  return <ThemeSchemeContext.Provider value={value}>{children}</ThemeSchemeContext.Provider>;
};

export const useScheme = (): ThemeSchemeContextInterface => {
  const context = useContext(ThemeSchemeContext);
  if (context === undefined) {
    throw new Error('useScheme must be used within a ThemeSchemeProvider');
  }
  return context;
};
